import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Lock, LogOut, Mail, RefreshCw, Search, ShieldCheck, ArrowLeft, AlertCircle, Flame, Download } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'bigAdminToken';

const CRITICAL_URGENCY = 'Critical (< 30 days)';
const isCritical = (item) => item?.type === 'africa_situation' && item?.urgency === CRITICAL_URGENCY;

const TYPE_META = {
  teaser_download:    { label: 'Teaser Download',      color: '#7c3aed', accent: '#ede9fe' },
  briefing_capital:   { label: 'Capital Briefing',     color: '#2f9e8e', accent: '#e8f5f3' },
  briefing_sovereign: { label: 'Sovereign Briefing',   color: '#c19a3c', accent: '#fcf5e3' },
  briefing_corporate: { label: 'U.S. Corp Briefing',   color: '#0d6efd', accent: '#e6efff' },
  partner:            { label: 'Partner Inquiry',      color: '#2f9e8e', accent: '#e8f5f3' },
  proposal:           { label: 'Investment Proposal',  color: '#1a365d', accent: '#e3eaf3' },
  africa_investor:    { label: 'BASS · Investor',      color: '#0d6efd', accent: '#e6efff' },
  africa_situation:   { label: 'BASS · Situation',     color: '#a83232', accent: '#fbe9e9' },
};

function formatDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

const HIDDEN_FIELDS = new Set(['id', 'type', 'created_at', 'email', 'name', 'contactName', 'companyName', 'organization']);

function Detail({ item }) {
  const entries = Object.entries(item).filter(([k, v]) => !HIDDEN_FIELDS.has(k) && v !== '' && v != null);
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-3">
      {entries.map(([k, v]) => (
        <div key={k} className="flex flex-col">
          <dt className="text-xs uppercase tracking-wider text-gray-400">{k.replace(/([A-Z])/g, ' $1').trim()}</dt>
          <dd className="text-gray-800 whitespace-pre-wrap break-words">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function InquiryCard({ item }) {
  const meta = TYPE_META[item.type] || { label: item.type, color: '#666', accent: '#eee' };
  const primary = item.name || item.contactName || item.organization || item.companyName || '—';
  const sub = item.companyName || item.organization || item.company || '';
  const replySubject = encodeURIComponent(`Re: ${meta.label} — ${primary}`);
  const critical = isCritical(item);

  return (
    <article
      className={`bg-white rounded-xl p-5 hover:shadow-md transition-shadow ${critical ? 'border-2 border-red-400 shadow-[0_0_0_4px_rgba(239,68,68,0.12)]' : 'border border-gray-200'}`}
      data-testid={`admin-inquiry-card-${item.id}`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="text-xs font-bold uppercase tracking-widest px-2 py-1 rounded" style={{ background: meta.accent, color: meta.color }}>
              {meta.label}
            </span>
            {critical && (
              <span
                className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest px-2 py-1 rounded bg-red-600 text-white animate-pulse"
                data-testid={`admin-critical-badge-${item.id}`}
              >
                <Flame className="w-3 h-3" /> Critical
              </span>
            )}
            <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
          </div>
          <h3 className="font-serif text-lg font-bold text-[#1a365d] truncate">{primary}</h3>
          {sub && <p className="text-sm text-gray-600 truncate">{sub}</p>}
          {item.email && (
            <a href={`mailto:${item.email}?subject=${replySubject}`}
              className="inline-flex items-center gap-1.5 text-sm text-[#2f9e8e] hover:text-[#1a365d] mt-1.5"
              data-testid={`admin-reply-${item.id}`}>
              <Mail className="w-3.5 h-3.5" /> {item.email}
            </a>
          )}
        </div>
      </div>
      <Detail item={item} />
    </article>
  );
}

function FilterChip({ active, label, count, onClick, color }) {
  return (
    <button onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${active ? 'text-white border-transparent' : 'text-gray-700 bg-white border-gray-200 hover:border-gray-400'}`}
      style={active ? { background: color || '#1a365d' } : {}}
      data-testid={`admin-filter-${(label || 'all').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      {label} <span className="opacity-70 ml-1">{count}</span>
    </button>
  );
}

const AdminInquiries = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || '');
  const [tokenInput, setTokenInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const authed = !!token;

  const load = useCallback(async (tokenToUse) => {
    setLoading(true);
    setError('');
    try {
      const t = tokenToUse || token;
      const res = await axios.get(`${API}/admin/inquiries`, {
        headers: { Authorization: `Bearer ${t}` },
        params: { limit: 300 }
      });
      setItems(res.data.items || []);
      setCounts(res.data.counts || {});
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken('');
        setAuthError('Token rejected. Please re-enter.');
      } else {
        setError(err?.response?.data?.detail || 'Failed to load inquiries.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (authed) load();
  }, [authed, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    const trimmed = tokenInput.trim();
    if (!trimmed) { setAuthError('Enter the admin token.'); return; }
    try {
      await axios.post(`${API}/admin/verify`, {}, { headers: { Authorization: `Bearer ${trimmed}` } });
      localStorage.setItem(TOKEN_KEY, trimmed);
      setToken(trimmed);
      setTokenInput('');
    } catch (err) {
      setAuthError(err?.response?.data?.detail || 'Invalid token.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken('');
    setItems([]);
    setCounts({});
  };

  const criticalCount = useMemo(() => items.filter(isCritical).length, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (typeFilter === 'critical') {
      list = list.filter(isCritical);
    } else if (typeFilter !== 'all') {
      list = list.filter((i) => i.type === typeFilter);
    }
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((i) => Object.values(i).some((v) => typeof v === 'string' && v.toLowerCase().includes(s)));
    }
    return list;
  }, [items, typeFilter, search]);

  const handleExportCSV = () => {
    if (filtered.length === 0) return;
    const keys = Array.from(new Set(filtered.flatMap((it) => Object.keys(it))));
    const ordered = ['type', 'created_at', 'id', ...keys.filter((k) => !['type', 'created_at', 'id'].includes(k))];
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const header = ordered.join(',');
    const rows = filtered.map((item) => ordered.map((k) => escape(item[k])).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const suffix = typeFilter === 'all' ? 'all' : typeFilter;
    a.href = url;
    a.download = `berkshire-inquiries-${suffix}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0d1f3a] flex items-center justify-center px-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8" data-testid="admin-login">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-gray-700 text-sm mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <div className="w-14 h-14 rounded-2xl bg-[#1a365d] flex items-center justify-center mb-5">
            <Lock className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-serif text-2xl font-bold text-[#1a365d] mb-1">Admin Access</h1>
          <p className="text-sm text-gray-600 mb-6">Token-protected viewer for inbound inquiries and situations.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Admin Token</label>
              <input type="password" autoFocus value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
                placeholder="Enter admin token"
                data-testid="admin-token-input" />
            </div>
            {authError && (
              <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3" data-testid="admin-login-error">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> {authError}
              </div>
            )}
            <button type="submit"
              className="w-full bg-[#1a365d] hover:bg-[#142c4d] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2"
              data-testid="admin-login-btn">
              <ShieldCheck className="w-4 h-4" /> Access Dashboard
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f9fb]">
      {/* Top bar */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-gray-400 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-serif text-xl font-bold text-[#1a365d]">Inquiries</h1>
              <p className="text-xs text-gray-500">Inbound submissions across all forms</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} disabled={loading}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
              data-testid="admin-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={handleExportCSV} disabled={filtered.length === 0}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title={filtered.length === 0 ? 'No rows to export' : `Export ${filtered.length} row${filtered.length === 1 ? '' : 's'} as CSV`}
              data-testid="admin-export-csv-btn">
              <Download className="w-4 h-4" /> CSV
            </button>
            <button onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-gray-600"
              data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto px-6 pb-4 flex flex-wrap items-center gap-2">
          <FilterChip active={typeFilter === 'all'} label="All" count={counts.all || 0} onClick={() => setTypeFilter('all')} />
          {criticalCount > 0 && (
            <button onClick={() => setTypeFilter('critical')}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${typeFilter === 'critical' ? 'bg-red-600 text-white border-transparent' : 'bg-red-50 text-red-700 border-red-200 hover:border-red-400'} ${typeFilter !== 'critical' ? 'animate-pulse' : ''}`}
              data-testid="admin-filter-critical">
              <Flame className="w-3 h-3" /> Critical <span className="opacity-80 ml-0.5">{criticalCount}</span>
            </button>
          )}
          {Object.entries(TYPE_META).map(([key, meta]) => (
            <FilterChip key={key}
              active={typeFilter === key}
              label={meta.label}
              count={counts[key] || 0}
              color={meta.color}
              onClick={() => setTypeFilter(key)} />
          ))}
          <div className="flex-1" />
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search..."
              className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-[#2f9e8e]"
              data-testid="admin-search-input" />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6">{error}</div>
        )}
        {loading && items.length === 0 ? (
          <div className="text-gray-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center" data-testid="admin-empty-state">
            <p className="text-gray-500">No inquiries match the current filter.</p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-inquiries-list">
            {filtered.map((item) => <InquiryCard key={item.id} item={item} />)}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminInquiries;
